import React, { useEffect, useState } from "react";
import { Slider } from "./ui/slider";
import * as THREE from "three";
import { VertexIcon, EdgeIcon, PolyIcon } from "./Icons";
import { uploadFileToStorage } from "../utils/upload";
import { Button } from "./ui/button";

/**
 * Displays vertex, edge, and polygon counts for a loaded 3D model,
 * and includes a "Reduce Faces" feature to simplify model geometry.
 */
function PolyCount({
  modelRef,
  modelLoaded,
  meshURL,  // URL of the current mesh
  onMeshUpdate,
  showToastMessage,
}: {
  modelRef: React.RefObject<THREE.Group>;
  modelLoaded: boolean;
  meshURL: string;
  onMeshUpdate: (meshURL: string) => void;
  showToastMessage: (message: string, duration?: number) => void;
}) {
  const [counts, setCounts] = useState({ vertices: 0, edges: 0, polys: 0 });
  const [reductionPercentage, setReductionPercentage] = useState(25);
  const [isReducing, setIsReducing] = useState(false);

  useEffect(() => {
    setReductionPercentage(25); 
  }, [meshURL]);

  // Reduces the number of faces in a 3D model using PyMeshLab endpoint
  const handleReduceFaces = async () => {
    try {
      setIsReducing(true);
      showToastMessage("Reducing Mesh Faces...");

      console.log("Starting reduction with:", {
        mesh_url: meshURL,
        reduction_percent: 100-reductionPercentage
      });

      // Reduced model from pymeshlab endpoint
      const response = await fetch('https://exaflop-labs--instant3d-blender-api-fastapi-app.modal.run/api/simplify-mesh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          mesh_url: meshURL,
          reduction_percent: 100-reductionPercentage
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        console.error('API Error:', errorData);
        throw new Error('Failed to reduce mesh');
      }
      
      const reducedBlob = await response.blob();
      const reducedFile = new File([reducedBlob], "reduced-result.glb"); 
      const reducedMeshURL = await uploadFileToStorage(reducedFile, "reduced-result.glb");
      onMeshUpdate(reducedMeshURL);  // Update parent component

    } catch (error) {
      console.error('Error reducing mesh:', error);
      showToastMessage("Error reducing mesh. Please try again.");
    } finally {
      setIsReducing(false);
    }
  };

  useEffect(() => {
    console.log("PolyCount effect running");
    console.log("modelRef.current:", modelRef.current);

    if (modelLoaded && modelRef.current) {
      let totalVertices = 0;
      let totalPolys = 0;
      let totalEdges = 0;
      console.log("Starting traverse");
      modelRef.current.traverse((child) => {
        if (child instanceof THREE.Mesh && child.geometry) {
          const geometry = child.geometry;
          // Vertex count
          totalVertices += geometry.attributes.position.count;
          // Poly (triangle) count
          totalPolys += geometry.index
            ? geometry.index.count / 3
            : geometry.attributes.position.count / 3;
          // Edge count (approximate: each triangle has 3 edges, but edges are shared)
          totalEdges += geometry.index
            ? geometry.index.count
            : geometry.attributes.position.count;
        }
      });

      console.log("Counts calculated:", {
        totalVertices,
        totalPolys,
        totalEdges,
      });
      setCounts({
        vertices: Math.round(totalVertices),
        edges: Math.round(totalEdges / 2), // Divide by 2 as edges are shared
        polys: Math.round(totalPolys),
      });
    }
  }, [modelLoaded, modelRef, meshURL]);

  return (
    <div className="absolute top-4 right-4 bg-secondary-background rounded-lg px-4 py-2 text-foreground text-sm z-50 border-2 border-border neo-shadow w-[190px]">
      <div className="flex flex-col gap-1">
        <div className="flex justify-between">
          <span className="text-foreground/70 flex gap-2 items-center">
            <VertexIcon />
            Vertices:
          </span>
          <span className="ml-3 font-medium">
            {counts.vertices.toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-foreground/70 flex gap-2 items-center">
            <EdgeIcon />
            Edges:
          </span>
          <span className="ml-3 font-medium">
            {counts.edges.toLocaleString()}
          </span>
        </div>
        <div className="flex justify-between">
          <span className="text-foreground/70 flex gap-2 items-center">
            <PolyIcon />
            Polys:
          </span>
          <span className="ml-3 font-medium">
            {counts.polys.toLocaleString()}
          </span>
        </div>
      </div>

      {/* Reduce Faces Slider */}
      <div className="mt-4 border-t border-[#3a396f] pt-4">
        <div className="text-foreground/80 mb-2">
            Reduce Faces by {reductionPercentage}%
          </div>
          <Slider
            value={[reductionPercentage]}
            onValueChange={(v) => setReductionPercentage(v[0] ?? 25)}
            min={1}
            max={100}
            step={1}
            className="max-w-md"
          />
          <Button onClick={handleReduceFaces} disabled={isReducing} className="w-full mt-3">
            {isReducing ? "Reducing..." : "Reduce Faces"}
          </Button>
      </div>
    </div>
  );
}

export default PolyCount; 
